<template>
  <VaLayout
    class="h-full"
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile" class="p-4" />
      <main class="p-4 pt-0 h-full min-w-0 max-w-full overflow-x-hidden">
        <article class="app-content min-w-0 max-w-full">
          <RouterView />
        </article>
      </main>
    </template>

    <template #footer>
      <div class="flex items-center justify-center gap-3 text-sm py-2">
        <span class="text-center text-gray-600">
          &copy; {{ new Date().getFullYear() }} Desarrollado por Biznet IT. Todos los derechos reservados.
        </span>
        <span class="text-gray-300 hidden sm:inline">|</span>
        <a
          href="#"
          class="text-primary font-medium hover:underline cursor-pointer"
          @click.prevent="showAvisoModal = true"
        >
          Aviso de Protección de Datos Personales
        </a>
        <span class="text-gray-300 hidden sm:inline">|</span>
        <a
          href="#"
          class="text-primary font-medium hover:underline cursor-pointer"
          @click.prevent="showReglamentoModal = true"
        >
          Reglamento Muelle Prat
        </a>
      </div>
    </template>
  </VaLayout>
  <div
    class="w-full justify-center items-center gap-3 bg-[var(--va-background-secondary)] shadow-[0px_-1px_2px_#adadad6b] p-3 flex flex-wrap sticky bottom-0 text-sm"
  >
    <span class="text-center text-gray-600">
      &copy; {{ new Date().getFullYear() }} Desarrollado por Biznet IT. Todos los derechos reservados.
    </span>
    <span class="text-gray-300 hidden sm:inline">|</span>
    <a href="#" class="text-primary font-medium hover:underline cursor-pointer" @click.prevent="showAvisoModal = true">
      Aviso de Protección de Datos Personales
    </a>
    <span class="text-gray-300 hidden sm:inline">|</span>
    <a
      href="#"
      class="text-primary font-medium hover:underline cursor-pointer"
      @click.prevent="showReglamentoModal = true"
    >
      Reglamento Muelle Prat
    </a>
  </div>

  <!-- Modal Aviso de Protección de Datos (PP-RA-03) -->
  <VaModal v-model="showAvisoModal" size="large" mobile-fullscreen close-button hide-default-actions>
    <h1 class="va-h5 mb-4 text-primary font-bold">Aviso de Protección de Datos Personales</h1>
    <div class="space-y-4 text-sm text-gray-700 leading-relaxed max-h-[60vh] overflow-y-auto pr-2">
      <p>
        <strong>Empresa Portuaria Valparaíso (EPV)</strong>, en cumplimiento con la
        <strong>Ley N° 19.628 sobre Protección de la Vida Privada</strong> y el Reglamento de Operación y Acceso de
        Muelle Prat, informa a todos los armadores, tripulantes y usuarios del sistema lo siguiente:
      </p>
      <h3 class="font-bold text-gray-900 mt-3">1. Finalidad del Tratamiento de Datos</h3>
      <p>
        Los datos personales, imágenes y documentos recolectados (RUT, certificados de navegabilidad, matrículas y
        registros de tripulación) tienen como único fin la verificación de antecedentes de seguridad, otorgamiento de
        Autorizaciones de Operación (AO) y el control de acceso al recinto portuario.
      </p>
      <h3 class="font-bold text-gray-900 mt-3">2. Principio de Minimización y Eliminación Automática</h3>
      <p>
        Los documentos de identificación personal (Cédula de Identidad) son resguardados de forma encriptada y
        <strong>eliminados automáticamente de forma irreversible</strong> una vez que la administración de EPV emite la
        resolución formal de aprobación o rechazo de la solicitud.
      </p>
      <h3 class="font-bold text-gray-900 mt-3">3. Derechos del Titular</h3>
      <p>
        El titular podrá ejercer en todo momento sus derechos de acceso, rectificación, cancelación y oposición conforme
        a la ley vigente, dirigiéndose a los canales oficiales de atención de EPV.
      </p>
    </div>
    <template #footer>
      <div class="flex justify-end mt-4">
        <VaButton color="primary" @click="showAvisoModal = false">Entendido y Cerrar</VaButton>
      </div>
    </template>
  </VaModal>

  <!-- Modal Reglamento Muelle Prat -->
  <VaModal v-model="showReglamentoModal" size="large" mobile-fullscreen close-button hide-default-actions>
    <h1 class="va-h5 mb-4 text-primary font-bold">Reglamento de Operación y Acceso — Muelle Prat (EPV)</h1>
    <div class="space-y-4 text-xs font-mono whitespace-pre-line text-gray-800 leading-relaxed max-h-[60vh] overflow-y-auto pr-2 p-3 bg-gray-50 rounded border">
      {{ reglamentoTexto }}
    </div>
    <template #footer>
      <div class="flex justify-end mt-4">
        <VaButton color="primary" @click="showReglamentoModal = false">Cerrar</VaButton>
      </div>
    </template>
  </VaModal>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'
import configuracionSistemaManager from '../services/api-services/ConfiguracionSistemaServices'

const GlobalStore = useGlobalStore()

const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const showAvisoModal = ref(false)
const showReglamentoModal = ref(false)
const reglamentoTexto = computed(() => configuracionSistemaManager.reglamentoAO)

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}
</style>
